import { LogicalFilter, QueryStringInput } from '@hello-aria/query-string'
import { Company } from '../../../shared/models/companyModel'
import {
    CreateCreditFields,
    CreateCreditGateway,
    Credit,
    UpdateCredit,
    UpdateCreditGateway
} from '../../../shared/models/creditModels'
import { AttachmentWithIdsGateway } from '../../../shared/models/uploadModels'
import { RequestResponse, requestService } from '../../../shared/services/requestServices'
import { uploadFileService } from '../../../shared/services/uploadServices'
import { resolveApiUrl } from '../../../shared/utils/requestsUtils'
import { isNil } from 'lodash'
import { removeWhiteSpace } from '../../../shared/utils/stringUtils'
import { Identifier } from '@/shared/models/debtorModels'
import { trackEvent } from '../../../shared/utils/analyticsUtils'
import { handleHttpError } from '@/shared/utils/queryUtils'

interface GetCredits {
    companyId?: Company['id']
    query?: QueryStringInput
    signal?: AbortSignal
}

export const mapCreditsService = (credits: Credit[] | undefined): Credit[] | undefined => {
    return credits?.map((credit) => {
        credit.remainingAmount =
            credit.amount && typeof credit.outstandingAmount === 'number'
                ? credit.amount - credit.outstandingAmount
                : undefined
        return credit
    })
}

export const getCreditsService = async (options?: GetCredits): Promise<RequestResponse<Credit[]> | undefined> => {
    try {
        const { query, companyId } = options || {}
        const url = resolveApiUrl('credit.getAll', {
            query: {
                ...query,
                sorts: query?.sorts ?? [
                    {
                        path: 'updatedAt',
                        direction: 'desc'
                    }
                ]
            },
            variables: { companyId }
        })
        const result: RequestResponse<Credit[] | undefined> | undefined = await requestService(
            url,
            options?.signal
                ? {
                      signal: options?.signal
                  }
                : undefined
        )

        return {
            ...result,
            body: mapCreditsService(result?.body)
        }
    } catch (error) {
        handleHttpError(getCreditsService.name, error)
    }
}

export const getCreditService = async (
    companyId: Company['id'],
    creditId: Credit['id'] | undefined | null
): Promise<RequestResponse<Credit> | undefined> => {
    try {
        if (isNil(creditId)) {
            throw new Error('Credit ID is required')
        }

        const url = resolveApiUrl('credit.get', { variables: { companyId, creditId } })

        const response = await requestService<Credit>(url)

        if (!response?.body) return response

        return {
            ...response,
            body: {
                ...response?.body,
                allocations: response?.body?.allocations.sort((a, b) =>
                    a.guarantor === 'aria' ? -1 : b.guarantor === 'aria' ? 1 : 0
                )
            }
        }
    } catch (error) {
        handleHttpError(getCreditService.name, error)
    }
}

export const postCreditService = async (
    payload: CreateCreditFields,
    companyId: Company['id']
): Promise<RequestResponse<Credit> | undefined> => {
    const url = resolveApiUrl('credit.create', { variables: { companyId } })
    const sanitizedPayload: CreateCreditFields = {
        claimedAmount: payload.claimedAmount,
        claimedCurrency: payload.claimedCurrency,
        debtorIdentifier: payload.debtorIdentifier,
        debtorId: payload.debtorId
    }

    const response = await requestService<Credit>(url, { method: 'POST', data: sanitizedPayload })

    trackEvent('Create_CreditLimit')

    return response
}

export const createCreditService = async (
    payload: CreateCreditGateway,
    companyId: Company['id']
): Promise<RequestResponse<Credit> | undefined> => {
    const url = resolveApiUrl('credit.create', { variables: { companyId } })
    const sanitizedPayload: CreateCreditGateway = {
        claimedAmount: payload.claimedAmount,
        claimedCurrency: payload.claimedCurrency,
        debtorIdentifier: payload.debtorIdentifier,
        debtorId: payload.debtorId,
        guarantor: payload.guarantor,
        ...(payload.attachments?.length && { attachments: payload.attachments })
    }

    const response = await requestService<Credit>(url, { method: 'POST', data: sanitizedPayload })

    trackEvent('Create_CreditLimit')

    return response
}

export const updateCreditService = async (
    payload: UpdateCredit,
    companyId: Company['id']
): Promise<RequestResponse<Credit> | undefined> => {
    const url = resolveApiUrl('credit.create', { variables: { companyId } })

    const attachments = payload.attachments ? await uploadFileService(payload.attachments, companyId) : null
    const attachmentIds: AttachmentWithIdsGateway[] | null =
        attachments?.map((attachment) => ({ id: attachment.fileId })) || []

    const sanitizedPayload: UpdateCreditGateway = {
        claimedAmount: payload.claimedAmount,
        claimedCurrency: payload.claimedCurrency,
        debtorIdentifier: payload.debtorIdentifier,
        debtorId: payload.debtorId ?? undefined,
        comment: payload.comment || null,
        attachments: attachmentIds,
        guarantor: payload.guarantor
    }

    const response = await requestService<Credit>(url, { method: 'POST', data: sanitizedPayload })

    trackEvent('Update_CreditLimit')

    return response
}

export const listCreditLimitsByDebtorIdentifier = async (params: {
    companyId: Company['id']
    debtorIdentifier: Identifier | undefined
}): Promise<RequestResponse<Credit[]>> => {
    const { companyId, debtorIdentifier } = params
    const filters: QueryStringInput['filters'] = []
    if (!isNil(debtorIdentifier)) {
        const additionalFilter: LogicalFilter = {
            type: 'logical',
            operator: 'and',
            filters: [
                {
                    path: 'debtorIdentifier.value',
                    type: 'comparison',
                    value: removeWhiteSpace(debtorIdentifier.value),
                    operator: 'eq'
                },
                {
                    path: 'debtorIdentifier.type',
                    type: 'comparison',
                    value: debtorIdentifier.type,
                    operator: 'eq'
                }
            ]
        }
        filters.push(additionalFilter)
    }

    if (filters.length === 0) {
        return { body: [] }
    }

    const url = resolveApiUrl('credit.getAll', { query: { filters }, variables: { companyId } })
    const response = await requestService<Credit[]>(url)

    return {
        ...response,
        body: mapCreditsService(response?.body)
    }
}
